import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getBusinessById,
  getServicesByBusiness,
  getStaffByBusiness,
  getAppointmentsByBusiness,
  createAppointment,
  requestPhoneVerificationCode,
  verifyPhoneVerificationCode,
  getBusinesses,
} from "../utils/api";
import {
  generateTimeSlots,
  minutesToTime,
  timeToMinutes,
} from "../utils/timeUtils";
import Calendar from "../components/Calendar";
import TimeSlotGrid from "../components/TimeSlotGrid";
import VerificationModal from "../components/VerificationModal";
import BusinessLogo from "../components/BusinessLogo";
import {
  ArrowLeft,
  MapPin,
  Phone,
  Clock,
  Star,
  CheckCircle2,
  ChevronRight,
  User,
  Banknote,
  Timer,
  X,
  SquareArrowOutUpRight,
} from "lucide-react";

export default function BusinessDetail() {
  const { url_string } = useParams();
  const navigate = useNavigate();

  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");

  const [timeSlots, setTimeSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [showVerification, setShowVerification] = useState(false);
  const [sendingVerificationCode, setSendingVerificationCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [isKvkkApproved, setIsKvkkApproved] = useState(false);

  const hasLocation = Boolean(business?.location);

  const handleLocationOpen = (e) => {
    if (!hasLocation) return;
    e.preventDefault();
    e.stopPropagation();
    window.open(business.location, "_blank", "noopener,noreferrer");
  };

  const handleLocationKeyDown = (e) => {
    if (!hasLocation) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      window.open(business.location, "_blank", "noopener,noreferrer");
    }
  };

  const getTurkeyNow = () => {
    const now = new Date();

    const dateParts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Istanbul",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);

    const hourParts = new Intl.DateTimeFormat("tr-TR", {
      timeZone: "Europe/Istanbul",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(now);

    const year = dateParts.find((p) => p.type === "year")?.value;
    const month = dateParts.find((p) => p.type === "month")?.value;
    const day = dateParts.find((p) => p.type === "day")?.value;
    const hour = parseInt(hourParts.find((p) => p.type === "hour")?.value ?? "0", 10);
    const minute = parseInt(hourParts.find((p) => p.type === "minute")?.value ?? "0", 10);

    return {
      date: `${year}-${month}-${day}`,
      minutes: hour * 60 + minute,
    };
  };

  const filteredStaffMembers = selectedService
    ? staffMembers.filter((member) =>
      Array.isArray(selectedService.staffList)
        ? selectedService.staffList.includes(member.id)
        : true
    )
    : [];

  // İşletme, hizmet ve çalışan verilerini yükle
  useEffect(() => {
    async function fetchData() {
      if (!url_string) {
        navigate("/");
        return;
      }

      try {
        setLoading(true);
        // Önce tüm işletmeleri alıp url_string üzerinden id'yi buluyoruz
        const allBusinesses = await getBusinesses();
        const bizItem = allBusinesses.find(b => b.url_string === url_string);

        if (!bizItem || !bizItem.id) {
          navigate("/");
          return;
        }

        const mappedId = bizItem.id;

        // id ile geri kalan verileri çekiyoruz
        const [biz, srvList, stfList] = await Promise.all([
          getBusinessById(mappedId),
          getServicesByBusiness(mappedId),
          getStaffByBusiness(mappedId),
        ]);

        if (!biz) {
          navigate("/");
          return;
        }

        setBusiness(biz);
        setServices(srvList);
        setStaffMembers(stfList);
      } catch (err) {
        console.error("İşletme yüklenemedi:", err);
        navigate("/");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [url_string, navigate]);

  // Tarih, hizmet veya çalışan değiştiğinde time slot'ları yeniden hesapla
  const loadTimeSlots = useCallback(async () => {
    if (!business || !selectedService || !selectedStaff || !selectedDate) {
      setTimeSlots([]);
      return;
    }

    try {
      setSlotsLoading(true);
      // Seçilen çalışanın o günkü randevularını getir
      const appointments = await getAppointmentsByBusiness(business.id, selectedDate, selectedStaff.id);
      // Çalışanın kendi çalışma saatlerini kullan
      const slots = generateTimeSlots(
        selectedStaff.workingHours,
        selectedService.duration,
        appointments
      );

      const turkeyNow = getTurkeyNow();
      const isTodayInTurkey = selectedDate === turkeyNow.date;

      const filteredSlots = slots.map((slot) => {
        if (!isTodayInTurkey) {
          return slot;
        }

        const isFuture = timeToMinutes(slot.time) > turkeyNow.minutes;
        return {
          ...slot,
          available: slot.available && isFuture,
        };
      });

      setTimeSlots(filteredSlots);
    } catch (err) {
      console.error("Saat dilimleri yüklenemedi:", err);
      setTimeSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [business, selectedService, selectedStaff, selectedDate]);

  useEffect(() => {
    loadTimeSlots();
  }, [loadTimeSlots]);

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setSelectedStaff(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setStep(2);
  };

  const handleStaffSelect = (staff) => {
    setSelectedStaff(staff);
    setSelectedDate(null);
    setSelectedTime(null);
    setStep(3);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setStep(4);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(5);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customerName.trim() || !customerPhone.trim()) return;
    setSubmitError("");

    if (!isKvkkApproved) {
      setSubmitError("Lütfen işleme devam etmek için KVKK Aydınlatma Metni'ni onaylayın.");
      return;
    }

    try {
      setSendingVerificationCode(true);
      await requestPhoneVerificationCode(customerPhone.trim());
      setShowVerification(true);
    } catch (err) {
      setSubmitError(err.message || "Doğrulama kodu gönderilemedi.");
    } finally {
      setSendingVerificationCode(false);
    }
  };

  const handleVerifyCode = async (code) => {
    await verifyPhoneVerificationCode(customerPhone.trim(), code);
  };

  const handleVerified = async () => {
    const startMinutes =
      parseInt(selectedTime.split(":")[0]) * 60 +
      parseInt(selectedTime.split(":")[1]);
    const endTime = minutesToTime(startMinutes + selectedService.duration);

    try {
      setSubmitting(true);
      await createAppointment({
        businessId: business.id,
        serviceId: selectedService.id,
        staffId: selectedStaff.id,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim(),
        date: selectedDate,
        startTime: selectedTime,
        endTime: endTime,
        status: "confirmed",
        isKvkkApproved: isKvkkApproved,
      });

      setShowVerification(false);

      setTimeout(() => {
        setStep(1);
        setSelectedService(null);
        setSelectedStaff(null);
        setSelectedDate(null);
        setSelectedTime(null);
        setCustomerName("");
        setCustomerPhone("");
        navigate("/");
      }, 500);
    } catch (err) {
      setShowVerification(false);
      setSubmitError(err.message || "Randevu oluşturulurken bir hata oluştu.");
      console.error("Randevu hatası:", err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
        <p style={{ marginTop: "1rem", color: "#94a3b8" }}>Yükleniyor...</p>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="center-screen">
        <p style={{ color: "#ef4444" }}>İşletme bulunamadı.</p>
      </div>
    );
  }

  const steps = [
    { num: 1, label: "Hizmet" },
    { num: 2, label: "Çalışan" },
    { num: 3, label: "Tarih" },
    { num: 4, label: "Saat" },
    { num: 5, label: "Bilgiler" },
  ];

  return (
    <div>
      {/* Business Header */}
      <div className="detail-header">
        <div className="detail-header-inner">
          <button onClick={() => navigate("/")} className="detail-back">
            <ArrowLeft />
            Geri Dön
          </button>

          <div className="detail-info">
            <BusinessLogo businessId={business.id} className="detail-avatar" />
            <div style={{ flex: 1 }}>
              <h1 className="detail-name">{business.name}</h1>
              <p className="detail-desc">{business.description}</p>
              <div className="detail-meta">
                <span
                  className={hasLocation ? "detail-meta-item-clickable" : ""}
                  onClick={hasLocation ? handleLocationOpen : undefined}
                  onKeyDown={hasLocation ? handleLocationKeyDown : undefined}
                  role={hasLocation ? "button" : undefined}
                  tabIndex={hasLocation ? 0 : undefined}
                  style={hasLocation ? { cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" } : { display: "inline-flex", alignItems: "center", gap: "4px" }}
                  title={hasLocation ? "Konuma git" : undefined}
                >
                  <MapPin className="icon-white" /> {business.address}
                  {hasLocation && (
                    <SquareArrowOutUpRight className="icon-white" size={16} />
                  )}
                </span>
                <span><Phone className="icon-white" /> {business.phone}</span>
                <span><Clock className="icon-white" /> {business.workingHours.start} - {business.workingHours.end}</span>
                <span>
                  <Star className="rating-gold" />
                  <span className="text-gold">{Number(business.rating).toFixed(1)} ({business.reviewCount})</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="step-container">
        {/* Step Indicator */}
        <div className="step-indicator">
          {steps.map((s, i) => {

            let btnClass = "step-btn";
            let stepNumClass = "step-num";
            let stepClassStr = ""

            if (s.num === step) stepClassStr += " current";
            else if (s.num < step) stepClassStr += " completed";
            else stepClassStr += " upcoming";

            btnClass += stepClassStr;
            stepNumClass += stepClassStr;

            return (
              <React.Fragment key={s.num}>
                <button
                  onClick={() => { if (s.num < step) setStep(s.num); }}
                  disabled={s.num > step}
                  className={btnClass}
                >
                  <span className={stepNumClass}>
                    {s.num < step ? <CheckCircle2 /> : s.num}
                  </span>
                  <span className="step-label">{s.label}</span>
                </button>
                {i < steps.length - 1 && <ChevronRight className="step-chevron" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 1: Service Selection */}
        <div className={step === 1 ? "" : "hidden"}>
          <h2 className="section-title">Hizmet Seçin</h2>
          <div className="service-list">
            {services.map((service) => (
              <button
                key={service.id}
                onClick={() => handleServiceSelect(service)}
                className={`service-item ${selectedService?.id === service.id ? "selected" : ""}`}
              >
                <div>
                  <h3 className="service-name">{service.name}</h3>
                  <div className="service-details">
                    <span><Timer /> {service.duration} dk</span>
                    <span><Banknote /> {service.price} ₺</span>
                  </div>
                </div>
                <ChevronRight className="service-item-arrow" />
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Staff Selection */}
        <div className={step === 2 ? "" : "hidden"}>
          <h2 className="section-title">Çalışan Seçin</h2>
          <p className="section-subtitle">
            <span className="highlight">{selectedService?.name}</span> hizmeti için bir çalışan seçin.
          </p>
          {filteredStaffMembers.length === 0 && (
            <div className="empty-state" style={{ marginBottom: "1rem" }}>
              <p>Bu hizmet için atanmış çalışan bulunmuyor.</p>
            </div>
          )}
          <div className="staff-list">
            {filteredStaffMembers.map((member) => (
              <button
                key={member.id}
                onClick={() => handleStaffSelect(member)}
                className={`staff-card ${selectedStaff?.id === member.id ? "selected" : ""}`}
              >
                <div className="staff-card-avatar">
                  <User />
                </div>
                <div className="staff-card-info">
                  <h3 className="staff-card-name">{member.fullName}</h3>
                  {/*<span className={`role-badge small ${member.role}`}>
                    <Shield />
                    {member.role === "admin" ? "Yönetici" : "Personel"}
                  </span>*/}
                </div>
                <ChevronRight className="service-item-arrow" />
              </button>
            ))}
          </div>
        </div>

        {/* Step 3: Date Selection */}
        <div className={step === 3 ? "" : "hidden"}>
          <h2 className="section-title">Tarih Seçin</h2>
          <p className="section-subtitle">
            <span className="highlight">{selectedStaff?.fullName}</span> için uygun bir gün seçin.
          </p>
          <Calendar
            selectedDate={selectedDate}
            onSelectDate={handleDateSelect}
            closedDays={selectedStaff?.closedDays || business.closedDays}
            maxAdvanceDays={selectedStaff?.maxBookingDays || 10}
          />
        </div>

        {/* Step 4: Time Selection */}
        <div className={step === 4 ? "" : "hidden"}>
          <h2 className="section-title">Saat Seçin</h2>
          <p className="section-subtitle">
            <span className="highlight">{selectedStaff?.fullName}</span> için uygun saat dilimlerini aşağıda görebilirsiniz.
          </p>
          {slotsLoading ? (
            <div className="center-screen" style={{ minHeight: "150px" }}>
              <div className="spinner" />
              <p style={{ marginTop: "1rem", color: "#94a3b8" }}>Saatler yükleniyor...</p>
            </div>
          ) : (
            <TimeSlotGrid
              slots={timeSlots}
              selectedTime={selectedTime}
              onSelectTime={handleTimeSelect}
            />
          )}
        </div>

        {/* Step 5: Customer Info */}
        {step === 5 && (
          <div>
            <h2 className="section-title">Bilgilerinizi Girin</h2>
            <p className="section-subtitle">
              Randevunuz için kişisel bilgilerinizi girin.
            </p>

            {/* Submit Error */}
            {submitError && (
              <div className="empty-state" style={{ marginBottom: "1rem" }}>
                <p style={{ color: "#ef4444" }}>{submitError}</p>
              </div>
            )}

            {/* Appointment Summary */}
            <div className="summary-box">
              <h3 className="summary-title">Randevu Özeti</h3>
              <div className="summary-grid">
                <div>
                  <span className="label">Hizmet:</span>
                  <p className="value">{selectedService?.name}</p>
                </div>
                <div>
                  <span className="label">Çalışan:</span>
                  <p className="value">{selectedStaff?.fullName}</p>
                </div>
                <div>
                  <span className="label">Ücret:</span>
                  <p className="value">{selectedService?.price} ₺</p>
                </div>
                <div>
                  <span className="label">Tarih:</span>
                  <p className="value">{selectedDate}</p>
                </div>
                <div>
                  <span className="label">Saat:</span>
                  <p className="value">{selectedTime}</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">
                  <User /> Ad Soyad
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Örn: Ahmet Yılmaz"
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <Phone /> Telefon Numarası
                </label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="05XX XXX XX XX"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group kvkk-group">
                <label className="kvkk-checkbox-label">
                  <input
                    type="checkbox"
                    checked={isKvkkApproved}
                    onChange={(e) => setIsKvkkApproved(e.target.checked)}
                  />
                  <span>
                    Randevu işlemlerimin tamamlanması için <a href="https://rezervioo.com/official_documents/rezervioo_kvkk.pdf" target="_blank" rel="noopener noreferrer" className="link-button">KVKK Aydınlatma Metni</a>'ni okudum ve telefon numaramın yalnızca doğrulama (SMS OTP) amacıyla işlenmesini onaylıyorum.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={submitting || sendingVerificationCode}
              >
                {sendingVerificationCode ? "Kod Gönderiliyor..." : "Randevuyu Onayla"}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Verification Modal */}
      <VerificationModal
        isOpen={showVerification}
        onClose={() => setShowVerification(false)}
        onVerified={handleVerified}
        onVerifyCode={handleVerifyCode}
      />
    </div>
  );
}
